/* Reading Interactive Brokers' Flex statement.
 *
 * §1 rules out broker credentials, and the exception is argued once, in
 * `lib/brokers.ts`, for every key this app holds: read-only or not at all. A
 * Flex token is a report key — it cannot place an order, move money, or change
 * anything, and the worst it can do in the wrong hands is tell someone what is
 * already in this book. A key that could trade is a different object and is
 * still refused.
 *
 * Parsed by pulling attributes off the <Trade> elements rather than through a
 * DOM. The statement is a flat list of self-closing tags, a parser for that is
 * a dozen lines, and doing it this way means the whole thing is testable without
 * a browser. */

import type { Account, Trade } from '../types';
import { tradeId } from './importTrades';

export interface FlexResult {
  trades: Trade[];
  skipped: Array<{ line: number; why: string }>;
  /** The account the statement says it covers, for checking against your own. */
  accountId: string | null;
}

/** Attributes off one self-closing element. */
function attributes(tag: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([A-Za-z][A-Za-z0-9_]*)="([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(tag)) !== null) out[m[1]!] = m[2]!;
  return out;
}

/** IBKR writes 20260603; some queries write 2026-06-03. Both are unambiguous,
    which is the whole reason this is easier than a broker's CSV. */
function toIsoDate(raw: string): string | null {
  const v = raw.trim();
  if (/^\d{8}$/.test(v)) return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  return null;
}

function toNumber(raw: string | undefined): number | null {
  if (raw === undefined || raw.trim() === '') return null;
  const n = Number(raw.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

export function parseFlex(xml: string, account: Account): FlexResult {
  const skipped: FlexResult['skipped'] = [];
  const trades: Trade[] = [];

  const accountMatch = /accountId="([^"]+)"/.exec(xml);
  const accountId = accountMatch?.[1] ?? null;

  /* IBKR returns an error document with the same 200 status as a statement, so
     an unread error would look like a statement with no trades in it. */
  const error = /<code>(\d+)<\/code>\s*<ErrorMessage>([^<]*)<\/ErrorMessage>/i.exec(xml)
    ?? /ErrorMessage="([^"]*)"/i.exec(xml);
  if (error) {
    return { trades: [], accountId, skipped: [{ line: 0, why: error[2] ?? error[1] ?? 'the broker returned an error' }] };
  }

  const elements = xml.match(/<Trade\b[^>]*\/?>/g) ?? [];
  elements.forEach((tag, i) => {
    const line = i + 1;
    const a = attributes(tag);

    // Cash, forex and corporate actions come through the same list. Only shares.
    const category = (a['assetCategory'] ?? 'STK').toUpperCase();
    if (category !== 'STK') return;

    const ticker = (a['symbol'] ?? '').trim().toUpperCase();
    if (!ticker) return skipped.push({ line, why: 'no symbol' });

    const at = toIsoDate(a['tradeDate'] ?? a['dateTime'] ?? '');
    if (!at) return skipped.push({ line, why: `no trade date on ${ticker}` });

    const quantity = toNumber(a['quantity']);
    if (quantity === null || quantity === 0) return skipped.push({ line, why: `no quantity on ${ticker}` });

    const price = toNumber(a['tradePrice']);
    if (price === null || price <= 0) return skipped.push({ line, why: `no price on ${ticker}` });

    /* buySell says it outright; where it is absent the sign of the quantity
       does, which is IBKR's own convention. */
    const flag = (a['buySell'] ?? '').toUpperCase();
    const side: Trade['side'] = flag === 'SELL' ? 'sell'
      : flag === 'BUY' ? 'buy'
      : quantity < 0 ? 'sell' : 'buy';

    const trade: Trade = {
      id: '',
      ticker,
      account,
      side,
      shares: Math.abs(quantity),
      price,
      at: `${at}T12:00:00.000Z`,
    };
    // The broker's own realised figure beats anything recomputed from averages.
    const realised = toNumber(a['fifoPnlRealized']);
    if (side === 'sell' && realised !== null && realised !== 0) trade.realizedGain = realised;

    trade.id = tradeId(trade);
    trades.push(trade);
  });

  return { trades, skipped, accountId };
}
